import React from 'react'
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid'
import {
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper
} from '@mui/material'
import { getRequest } from 'Includes/api/apicall'
import { GET_URL } from 'Includes/urls'
import { Link, useNavigate } from 'react-router-dom'
import {
  AddCircleOutlineOutlined,
  LocationOn,
  OpenInNew
} from '@mui/icons-material'

function CompanySecondaryDetailsList(props) {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sortModel, setSortModel] = React.useState([])
  const [billingStatusFilter, setBillingStatusFilter] = React.useState('')
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('')

  // 🔴 ORIGINAL FUNCTION (UNCHANGED)
  const extractCoordinates = (mapLocation) => {
    if (!mapLocation || !mapLocation.trim()) return null
    const trimmed = mapLocation.trim()

    // 1. Check if it's in "lat,lng" format
    if (/^-?\d+\.?\d*,-?\d+\.?\d*$/.test(trimmed)) {
      const parts = trimmed.split(',')
      return {
        lat: parseFloat(parts[0].trim()),
        lng: parseFloat(parts[1].trim())
      }
    }

    // 2. Check if it's a Google Maps URL containing exact pin coordinates !3d...!4d...
    const placeMatch = trimmed.match(/!3d([+-]?\d+\.?\d*)!4d([+-]?\d+\.?\d*)/)
    if (placeMatch) {
      return {
        lat: parseFloat(placeMatch[1]),
        lng: parseFloat(placeMatch[2])
      }
    }

    // 3. Check if it's a Google Maps URL with q=lat,lng
    const urlMatch = trimmed.match(/[?&]q=([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/)
    if (urlMatch) {
      return {
        lat: parseFloat(urlMatch[1]),
        lng: parseFloat(urlMatch[2])
      }
    }

    // 4. Check if it's a Google Maps URL with @lat,lng
    const atMatch = trimmed.match(/@([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/)
    if (atMatch) {
      return {
        lat: parseFloat(atMatch[1]),
        lng: parseFloat(atMatch[2])
      }
    }

    return null
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'company', headerName: 'Company ID', width: 120 },
    { field: 'company_name', headerName: 'Company', width: 220 },
    { field: 'primary_contact_name', headerName: 'Primary Contact', width: 200 },
    { field: 'primary_contact_phone_number', headerName: 'Primary Contact Phone', width: 200 },
    { field: 'employee1_name', headerName: 'Employee', width: 200 },
    { field: 'employee1_phone_number', headerName: 'Employee Phone', width: 200 },
    { field: 'client_billing_status', headerName: 'Billing Status', width: 200 },
    { field: 'billing_address', headerName: 'Billing Address', width: 260 },

    {
      field: 'map_location',
      headerName: 'Map Location',
      width: 240,
      renderCell: (params) => {
        const mapLocation = params.value
        if (!mapLocation) {
          return <Typography variant="body2" color="text.secondary">-</Typography>
        }

        const coords = extractCoordinates(mapLocation)
        const isUrl = mapLocation.startsWith('http://') || mapLocation.startsWith('https://')
        const googleMapsUrl = coords ? `https://www.google.com/maps?q=${coords.lat},${coords.lng}` : (isUrl ? mapLocation.trim() : null)

        if (googleMapsUrl) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn fontSize="small" color="primary" />
              <Typography
                component="a"
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Open Map
                <OpenInNew fontSize="small" />
              </Typography>
            </Box>
          )
        }

        return (
          <Typography variant="body2" title={mapLocation}>
            {mapLocation.length > 30 ? `${mapLocation.substring(0, 30)}...` : mapLocation}
          </Typography>
        )
      }
    },

    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          onClick={() => handleEdit(params?.row)}
          showInMenu
        />,
      ],
    },
  ]

  const handleEdit = (row) => {
    if (!row?.id) return
    navigate('/companysecondarydetails', { state: { detail: row.id } });
  }

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const loadRows = () => {
    setLoading(true)
    const params = {
      limit: pageSize,
      pageno: page + 1,
      page_size: pageSize,
      page: page + 1,
    }

    if (billingStatusFilter) {
      params.client_billing_status = billingStatusFilter
    }

    if (debouncedSearchTerm.trim()) {
      params.search = debouncedSearchTerm.trim()
    }

    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0]
      const field = sort.field
      const sortDir = sort.sort === 'asc' ? '' : '-'
      params.ordering = `${sortDir}${field}`
    }

    getRequest(GET_URL.companysecondarydetails.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || []
          const count =
            response.data?.count ??
            response.data?.total_count ??
            response.data?.total ??
            (Array.isArray(data) ? data.length : 0)
          setRows(Array.isArray(data) ? data : [])
          setRowCount(count)
        }
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    setPage(0)
  }, [billingStatusFilter, debouncedSearchTerm])

  React.useEffect(() => {
    loadRows()
  }, [billingStatusFilter, debouncedSearchTerm, page, pageSize, sortModel])

  const handlePageChange = (newPage) => setPage(newPage)
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize)
    setPage(0)
  }
  const handleSortModelChange = (model) => {
    setSortModel(model)
    setPage(0)
  }

  return (
    <Box sx={{ p: 3 }}>

      {/* HEADER */}
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Company Billing Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage company billing and contact information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/companysecondarydetails"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 2,
              py: 1,
              boxShadow: "0 4px 14px rgba(0,0,0,0.1)"
            }}
          >
            Add Details
          </Button>
        </Grid>
      </Grid>

      {/* FILTER CARD */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <Grid container spacing={2}>
          <Grid item md={3} xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Billing Status"
              value={billingStatusFilter}
              onChange={(e) => setBillingStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="GOOD">GOOD</MenuItem>
              <MenuItem value="MODERATE">MODERATE</MenuItem>
              <MenuItem value="CRITICAL">CRITICAL</MenuItem>
            </TextField>
          </Grid>

          <Grid item md={9} xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by company / contact / phone"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABLE */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) {
              handlePageSizeChange(model.pageSize)
            } else if (model.page !== page) {
              handlePageChange(model.page)
            }
          }}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          autoHeight
          rowHeight={42}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            }
          }}
        />
      </Paper>

    </Box>
  )
}

export default CompanySecondaryDetailsList;